import React, { useState } from 'react';
import axios from 'axios';
import { Check, Star, Upload, Loader2, Image as ImageIcon, Trash2 } from 'lucide-react';
import {
  THEME_PRESETS,
  FONT_OPTIONS,
  BACKGROUND_STYLES,
  CARD_STYLES,
  backgroundCss,
  cardCss,
  fontStack,
  withDefaults,
} from '../lib/themes';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Theme picker + live mini preview.
 *
 * Props:
 *   theme    — current theme object (may be partial; we fill defaults)
 *   onChange — (newTheme) => void.  Always pass the FULL theme back.
 */
const ThemePicker = ({ theme, onChange }) => {
  const t = withDefaults(theme);
  const [bgUploading, setBgUploading] = useState(false);
  const [bgError, setBgError] = useState('');
  const set = (patch) => onChange({ ...t, ...patch, preset: 'custom' });
  const applyPreset = (preset) =>
    onChange({
      ...t,
      ...preset.theme,
      // Preserve the user's existing logo unless the preset explicitly provides one.
      logo_url: preset.theme.logo_url || t.logo_url,
    });

  const handleBackgroundUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      setBgError('Background image is larger than 8 MB.');
      e.target.value = '';
      return;
    }
    setBgError('');
    setBgUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(`${API_URL}/api/upload`, formData, {
        withCredentials: true,
      });
      // Switch style to image automatically — that's the user's intent here.
      onChange({
        ...t,
        background_image: data.url,
        background_style: 'image',
        preset: 'custom',
      });
    } catch (err) {
      setBgError(err?.response?.data?.detail || 'Could not upload background.');
    } finally {
      setBgUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="space-y-6">
      {/* Presets */}
      <div>
        <h3 className="text-sm font-medium text-[#2C302E] mb-2">Theme presets</h3>
        <p className="text-xs text-[#8E958C] mb-3">
          Tap a preset to apply curated colors, fonts, and background in one click. You can still tweak anything below.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {THEME_PRESETS.map((p) => {
            const active = t.preset === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => applyPreset(p)}
                className={`text-left rounded-xl overflow-hidden border-2 transition-all ${
                  active ? 'border-[#7A8B7A] ring-2 ring-[#7A8B7A]/30' : 'border-[#E5E0D8] hover:border-[#7A8B7A]/50'
                }`}
                data-testid={`preset-${p.id}`}
                aria-pressed={active}
              >
                <div
                  className="h-20 flex items-end p-3"
                  style={backgroundCss(p.theme)}
                >
                  <div
                    className="h-2 w-2/3 rounded-full"
                    style={{ background: p.theme.primary_color }}
                  />
                </div>
                <div className="p-3 bg-white">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-[#2C302E]" style={{ fontFamily: fontStack(p.theme.font_family) }}>
                      {p.name}
                    </div>
                    {active && <Check size={14} className="text-[#7A8B7A]" />}
                  </div>
                  <div className="flex gap-1 mt-2">
                    <span className="w-4 h-4 rounded-full border border-black/5" style={{ background: p.theme.primary_color }} />
                    <span className="w-4 h-4 rounded-full border border-black/5" style={{ background: p.theme.secondary_color }} />
                    <span className="w-4 h-4 rounded-full border border-black/5" style={{ background: p.theme.background_color }} />
                  </div>
                  <p className="text-[11px] text-[#8E958C] mt-2 leading-tight">{p.description}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Background style + Card style */}
      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <h3 className="text-sm font-medium text-[#2C302E] mb-2">Background style</h3>
          <div className="grid grid-cols-2 gap-2">
            {BACKGROUND_STYLES.map((b) => {
              const active = t.background_style === b.id;
              return (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => set({ background_style: b.id })}
                  className={`text-left p-3 rounded-lg border-2 transition-colors ${
                    active ? 'border-[#7A8B7A] bg-[#7A8B7A]/5' : 'border-[#E5E0D8] hover:bg-[#FDFBF7]'
                  }`}
                  data-testid={`bg-style-${b.id}`}
                >
                  <div className="text-sm text-[#2C302E] font-medium">{b.label}</div>
                  <div className="text-[11px] text-[#8E958C]">{b.hint}</div>
                </button>
              );
            })}
          </div>

          {t.background_style === 'image' && (
            <div className="mt-3 p-3 rounded-lg border border-[#E5E0D8] bg-[#FDFBF7]">
              <div className="flex items-center gap-3 flex-wrap">
                {t.background_image ? (
                  <div className="relative">
                    <img
                      src={t.background_image.startsWith('http') ? t.background_image : `${API_URL}${t.background_image}`}
                      alt="Background preview"
                      className="h-16 w-28 object-cover rounded border border-[#E5E0D8]"
                      data-testid="bg-image-preview"
                    />
                    <button
                      type="button"
                      onClick={() => onChange({ ...t, background_image: '', preset: 'custom' })}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full"
                      data-testid="bg-image-remove"
                      title="Remove background"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ) : (
                  <div className="h-16 w-28 border-2 border-dashed border-[#E5E0D8] rounded flex items-center justify-center text-[#8E958C]">
                    <ImageIcon size={20} />
                  </div>
                )}
                <label className="flex items-center gap-2 px-3 py-2 border border-[#E5E0D8] rounded-lg cursor-pointer hover:bg-white transition-colors text-sm">
                  {bgUploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
                  <span>{bgUploading ? 'Uploading…' : t.background_image ? 'Replace' : 'Upload image'}</span>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={handleBackgroundUpload}
                    className="hidden"
                    disabled={bgUploading}
                    data-testid="bg-image-input"
                  />
                </label>
                <p className="text-xs text-[#8E958C]">JPG / PNG / WebP, up to 8&nbsp;MB. 1920&nbsp;×&nbsp;1080 or larger looks best.</p>
              </div>
              {bgError && (
                <p className="text-xs text-red-600 mt-2" data-testid="bg-image-error">{bgError}</p>
              )}
              {t.background_image && (
                <div className="mt-3">
                  <label className="block text-xs text-[#5C635A] mb-1">
                    Darkness overlay: {(Math.round((t.background_overlay ?? 0.35) * 100))}%
                  </label>
                  <input
                    type="range"
                    min={0}
                    max={0.8}
                    step={0.05}
                    value={t.background_overlay ?? 0.35}
                    onChange={(e) => onChange({ ...t, background_overlay: Number(e.target.value), preset: 'custom' })}
                    className="w-full accent-[#7A8B7A]"
                    data-testid="bg-image-overlay"
                  />
                  <p className="text-[11px] text-[#8E958C]">Increase if the questions are hard to read over the image.</p>
                </div>
              )}
            </div>
          )}
        </div>

        <div>
          <h3 className="text-sm font-medium text-[#2C302E] mb-2">Card style</h3>
          <div className="grid grid-cols-2 gap-2">
            {CARD_STYLES.map((c) => {
              const active = t.card_style === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => set({ card_style: c.id })}
                  className={`text-left p-3 rounded-lg border-2 transition-colors ${
                    active ? 'border-[#7A8B7A] bg-[#7A8B7A]/5' : 'border-[#E5E0D8] hover:bg-[#FDFBF7]'
                  }`}
                  data-testid={`card-style-${c.id}`}
                >
                  <div className="text-sm text-[#2C302E] font-medium">{c.label}</div>
                  <div className="text-[11px] text-[#8E958C]">{c.hint}</div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Font */}
      <div>
        <h3 className="text-sm font-medium text-[#2C302E] mb-2">Font</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {FONT_OPTIONS.map((f) => {
            const active = t.font_family === f.id;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => set({ font_family: f.id })}
                className={`p-3 rounded-lg border-2 transition-colors ${
                  active ? 'border-[#7A8B7A] bg-[#7A8B7A]/5' : 'border-[#E5E0D8] hover:bg-[#FDFBF7]'
                }`}
                data-testid={`font-${f.id}`}
              >
                <div className="text-base text-[#2C302E]" style={{ fontFamily: f.stack }}>
                  {f.sample}
                </div>
                <div className="text-[11px] text-[#8E958C] mt-1">{f.label}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Live preview */}
      <div>
        <h3 className="text-sm font-medium text-[#2C302E] mb-2">Live preview</h3>
        <p className="text-xs text-[#8E958C] mb-3">This is exactly how respondents will see your survey.</p>
        <div
          className="rounded-2xl overflow-hidden border border-[#E5E0D8]"
          data-testid="theme-preview"
        >
          <div className="p-8 sm:p-10" style={{ ...backgroundCss(t), fontFamily: fontStack(t.font_family) }}>
            <div className="max-w-md mx-auto">
              <h1 className="text-2xl mb-2" style={{ color: t.text_color, fontFamily: fontStack(t.font_family) }}>
                Patient Feedback
              </h1>
              <p className="text-sm mb-6" style={{ color: t.text_color, opacity: 0.7 }}>
                Your feedback helps us provide better care.
              </p>

              <div className="rounded-2xl p-5 mb-3" style={cardCss(t)}>
                <div className="text-sm font-medium mb-3" style={{ color: t.text_color }}>
                  How would you rate your visit?
                </div>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star
                      key={n}
                      size={26}
                      style={{ color: n <= 4 ? t.primary_color : t.text_color, opacity: n <= 4 ? 1 : 0.2 }}
                      fill={n <= 4 ? t.primary_color : 'none'}
                    />
                  ))}
                </div>
              </div>

              <div className="rounded-2xl p-5" style={cardCss(t)}>
                <div className="text-sm font-medium mb-3" style={{ color: t.text_color }}>
                  Anything we could improve?
                </div>
                <div
                  className="rounded-lg p-3 text-sm"
                  style={{
                    background: 'rgba(255,255,255,0.6)',
                    border: `1px solid ${t.text_color}22`,
                    color: t.text_color,
                  }}
                >
                  Tell us more…
                </div>
              </div>

              <button
                type="button"
                className="mt-5 px-6 py-2.5 rounded-full text-white text-sm font-medium pointer-events-none"
                style={{ background: t.primary_color }}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThemePicker;
